import {Select} from '@wandb/weave/components/Form/Select';
import React, {useCallback, useState} from 'react';
import {type InputActionMeta} from 'react-select';

import {useDebounceState} from '../../../util/hooks';
import {useRunSelectorProjectInfo} from '../RunSelectorRunSetContext';
import {
  type ProjectOption,
  useCrossProjectOptions,
} from './useCrossProjectOptions';

type ProjectPickerProps = {
  selectedProject: ProjectOption | null;
  onSelectProject: (project: ProjectOption | null) => void;
};

export const ProjectPicker = ({
  selectedProject,
  onSelectProject,
}: ProjectPickerProps) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const {entityName} = useRunSelectorProjectInfo();
  const [, debouncedQuery, setSearchInput] = useDebounceState('', 300);
  const {loading, options} = useCrossProjectOptions(entityName, debouncedQuery);

  const onInputChange = useCallback(
    (newValue: string, actionMeta: InputActionMeta) => {
      if (actionMeta.action === 'input-change') {
        setSearchInput(newValue);
      }
    },
    [setSearchInput]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && menuIsOpen) {
        e.stopPropagation(); // prevent ESC from closing the drawer
        setMenuIsOpen(false);
      }
    },
    [menuIsOpen]
  );

  return (
    <div>
      <label
        htmlFor="cross-project-picker"
        className="mb-4 block font-semibold">
        Source project
      </label>
      <Select<ProjectOption>
        inputId="cross-project-picker"
        menuIsOpen={menuIsOpen}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        getOptionValue={o => o.internalId}
        getOptionLabel={o => `${o.entityName}/${o.name}`}
        options={options}
        value={selectedProject}
        onChange={onSelectProject}
        onInputChange={onInputChange}
        onKeyDown={onKeyDown}
        isLoading={loading}
        placeholder="Select a project"
      />
    </div>
  );
};
